import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Loader,
  ModalComponentWrapper,
  FormFieldSelectInput,
  LanguageInfo,
} from '@/shared/components';
import { useCountriesDictionary, useDictionary } from '@/shared/api';
import {
  FILE_TYPE_OPTIONS,
  useGenerateStatementForm,
  useGenerateStatementModal,
  type FileTypeSelectItem,
  type GenerateStatementFormValues,
  type FileType,
} from '../model';
import { useGenerateStatement } from '../api';
import { computeInitialDocumentLink } from '../lib';
import { ChevronRightIcon } from 'lucide-react';
import {
  buildLanguageSelectOptions,
  buildPeriodSelectOptions,
  downloadFile,
  useAppSettingsStore,
  useDateLocale,
  useDateModal,
} from '@/shared/lib';
import {
  type DateFilter,
  type HistoryDateFilter,
  type HistoryDateFilterItem,
  type HistoryDateFilterSelectItem,
  type Language,
  type LanguageSelectOption,
  type OperationFilter,
} from '@/shared/types';
import { formatPeriodLabel } from '@/shared/utils';
import { DEFAULT_DATE_FORMAT } from '@/shared/config';
import { useCallback, useMemo, useState } from 'react';

interface Props {
  filter: OperationFilter;
  dateFilter?: HistoryDateFilter;
  dateFrom?: Date;
  dateTo?: Date;
  fileType?: FileType;
  lang?: Language;
  formValues?: GenerateStatementFormValues;
  statementLink?: string;
  onBack?: (dateFrom?: Date, dateTo?: Date) => void;
}

const GenerateStatementModal = ({
  filter,
  dateFilter,
  dateFrom,
  dateTo,
  fileType,
  lang,
  formValues,
  statementLink,
  onBack,
}: Props) => {
  const { t } = useTranslation();

  const { countriesData, isCountriesLoading, countriesError } =
    useCountriesDictionary();

  const { data: historyDateFilters, isLoading: isHistoryDateFiltersLoading } =
    useDictionary<HistoryDateFilterItem[]>({
      dictionaryName: 'historydatefilters',
    });

  const { getLanguage } = useAppSettingsStore();
  const { dateLocale } = useDateLocale();

  const { form, fileTypeValue } = useGenerateStatementForm({
    language: lang ?? getLanguage(),
    dateFilter,
    dateFrom,
    dateTo,
    fileType,
    formValues,
  });

  const { generateStatement, isPending } = useGenerateStatement({ filter });

  const { openDateModal } = useDateModal();
  const { openGenerateStatementModal } = useGenerateStatementModal();

  const initialDocumentLink = computeInitialDocumentLink({
    statementLink,
    dateFrom,
    dateTo,
  });

  const [documentLink, setDocumentLink] = useState<string>(initialDocumentLink);

  const getPeriodLabel = (value: HistoryDateFilter) =>
    t(`personalAccount.userProfile.myDocuments.statement.periods.${value}`);

  const getLabel = useCallback(
    (value: DateFilter) => {
      return historyDateFilters?.value.find(item => item.datefilter === value)
        ?.name;
    },
    [historyDateFilters?.value]
  );

  const periodSelectOptions = useMemo(() => {
    if (!historyDateFilters?.value) return [];

    return buildPeriodSelectOptions([
      ...historyDateFilters.value,
      {
        datefilter: 'custom',
        type: 'client',
        name: getPeriodLabel('custom'),
        shortName: getPeriodLabel('custom'),
      },
    ]);
  }, [historyDateFilters?.value, isHistoryDateFiltersLoading]);

  if (isCountriesLoading || isHistoryDateFiltersLoading || !countriesData) {
    return <Loader />;
  }

  if (countriesError) {
    return <div>Error</div>;
  }

  const clearDocumentLink = () => {
    if (documentLink) setDocumentLink('');
  };

  const handlePeriodSelect = (value: HistoryDateFilter) => {
    if (value !== 'custom') {
      form.setValue('datefrom', undefined);
      form.setValue('dateto', undefined);
      clearDocumentLink();
      return;
    }

    const formValues = form.getValues();

    openDateModal({
      onApply: (dateFrom?: Date, dateTo?: Date) =>
        openGenerateStatementModal({
          dateFrom,
          dateTo,
          onBack,
          formValues,
          statementLink: documentLink,
        }),
      onBack: () =>
        openGenerateStatementModal({
          onBack,
          formValues,
          statementLink: documentLink,
        }),
    });
  };

  const onSubmit = async (data: GenerateStatementFormValues) => {
    if (documentLink) {
      window.open(documentLink, '_blank');
      return;
    }

    try {
      const response = await generateStatement(data);

      if (isCsvType) {
        downloadFile(response?.link || '');
      } else {
        setDocumentLink(response?.link || '');
      }
    } catch (error) {
      clearDocumentLink();
      console.error(error);
    }
  };

  const languageOptions = buildLanguageSelectOptions(countriesData.value);

  const isCsvType = fileTypeValue === 'csv';
  const submitKey = isCsvType ? 'export' : documentLink ? 'open' : 'generate';

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.myDocuments.statement.title')}
      onBack={onBack}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col gap-[1.6rem]">
            <FormFieldSelectInput<LanguageSelectOption>
              name="lang"
              label={t('common.language.placeholder')}
              items={languageOptions}
              onValueChange={clearDocumentLink}
              renderItem={item => <LanguageInfo language={item} />}
              renderSelectedValue={value => (
                <LanguageInfo
                  language={languageOptions.find(item => item.value === value)}
                  className="gap-[0.8rem]"
                  iconClassName="w-[2.4rem] h-[2.4rem]"
                />
              )}
            />

            <FormFieldSelectInput<HistoryDateFilterSelectItem>
              name="datefilter"
              label={t(
                'personalAccount.userProfile.myDocuments.statement.dateFilterPlaceholder'
              )}
              items={periodSelectOptions}
              renderItem={item => (
                <div className="headline4 flex items-center justify-between w-full">
                  {item.name}
                  {item.value === 'custom' && (
                    <ChevronRightIcon className="size-8 text-tertiary" />
                  )}
                </div>
              )}
              renderSelectedValue={value => (
                <div className="headline4">
                  {value === 'custom'
                    ? formatPeriodLabel({
                        locale: dateLocale,
                        dateFrom: form.getValues('datefrom'),
                        dateTo: form.getValues('dateto'),
                        defaultLabel: getPeriodLabel('custom'),
                        dateFormat: DEFAULT_DATE_FORMAT,
                      })
                    : getLabel(value as DateFilter)}
                </div>
              )}
              showOptionCheckmark={false}
              onPointerDown={value =>
                handlePeriodSelect(value as HistoryDateFilter)
              }
            />

            <FormFieldSelectInput<FileTypeSelectItem>
              name="fileType"
              label={t(
                'personalAccount.userProfile.myDocuments.statement.fileTypePlaceholder'
              )}
              items={FILE_TYPE_OPTIONS}
              onValueChange={clearDocumentLink}
              disabled={FILE_TYPE_OPTIONS.length <= 1}
              renderItem={item => <div className="headline4">{item.label}</div>}
              renderSelectedValue={value => (
                <div className="headline4">
                  {FILE_TYPE_OPTIONS.find(item => item.value === value)?.label}
                </div>
              )}
            />

            <Button
              size="lg"
              variant={documentLink || isCsvType ? 'default' : 'secondary'}
              type="submit"
              className="mt-[1.6rem]"
              loading={isPending}>
              {t(
                `personalAccount.userProfile.myDocuments.statement.submitButton.${submitKey}`
              )}
            </Button>
          </div>
        </form>
      </Form>
    </ModalComponentWrapper>
  );
};

export { GenerateStatementModal };
